import { effectiveHealth, ciHealth, scoreTone } from './format';
import type { Initiative, WeeklyReport, Health } from '../types/trackerModels';

/** A Programme/Portfolio's own overall progress, averaged across its direct children's
 *  percentComplete - children with no real work yet (effectiveHealth 'Not Started') are
 *  excluded, same exclusion ciHealth() already applies one level down. Returns null when
 *  nothing is computable, same convention as ciHealth/milestoneCompletion. */
export function rollupProgress(children: Initiative[]): number | null {
  const values = children.filter((c) => effectiveHealth(c) !== 'Not Started').map((c) => c.percentComplete);
  if (values.length === 0) return null;
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

/** A Programme/Portfolio's own Health enum, derived from the same CI Health score/threshold
 *  rule already used for its Reports tab tiles (ciHealth + scoreTone's 80/50 cutoffs) rather
 *  than a third, bespoke threshold. */
export function rollupHealth(children: Initiative[]): Health {
  const score = ciHealth(children);
  if (score === null) return 'Not Started';
  const tone = scoreTone(score);
  return tone === 'success' ? 'On Track' : tone === 'warning' ? 'At Risk' : 'Blocked';
}

export interface CensusMilestoneChange {
  childId: string;
  childName: string;
  milestoneId: string;
  name: string;
  /** Status transition - from and to are equal (no status change) on a week where only
   *  completionPercent moved. Render fromPercent/toPercent instead in that case, not this. */
  from: string;
  to: string;
  fromPercent: number;
  toPercent: number;
  /** True when this row is an automatic "target date passed" flag rather than a real change -
   *  the two are rendered with different icons/tone. */
  late: boolean;
}

export interface CensusRequirementChange {
  childId: string;
  childName: string;
  requirementId: string;
  text: string;
  from: string;
  to: string;
}

export interface Census {
  milestoneChanges: CensusMilestoneChange[];
  requirementChanges: CensusRequirementChange[];
  totalChanges: number;
}

/** Which census categories touched a given tree row - drives the small pulse-dot indicators on
 *  each row, so a reader can tell what kind of thing moved without opening the full census list
 *  below. Takes the row's whole subtree, not just its own id: a Portfolio's census is built from
 *  Project-tier descendants (see InitiativeDetailPage.tsx), not its direct Programme children, so
 *  a Programme row's pulse dots have to check whether ANY of that Programme's own descendants
 *  appear in the census, not just the Programme's own id (which never will - a Programme owns no
 *  milestones/requirements itself). `subtreeIds` should include the row's own id too, so this
 *  still works correctly for Program tier, where the census source and the tree rows are the
 *  same set. */
export function censusCategoriesForSubtree(census: Census, subtreeIds: Set<string>): { milestone: boolean; requirement: boolean } {
  return {
    milestone: census.milestoneChanges.some((c) => subtreeIds.has(c.childId)),
    requirement: census.requirementChanges.some((c) => subtreeIds.has(c.childId)),
  };
}

const today = () => new Date().toISOString().slice(0, 10);

/** Milestone deltas for one child. Unlike requirements (below), a report's `milestoneUpdates`
 *  is only ever populated when that child has at least one weighted milestone - unweighted
 *  children always submit it empty (SubmitReportPage.tsx), so for those, no week-over-week
 *  milestone diff is possible from report data alone. "Late" flags are always computable
 *  though, straight from the child's own live `milestones[]`, independent of reports.
 *
 *  A change is either a status transition (Upcoming -> Done, etc.) OR a completionPercent move
 *  with status unchanged - most real weeks are the second kind, not the first: a milestone
 *  typically holds the same status (often "At Risk") for several consecutive reports while its
 *  percent climbs underneath, and only flips status once (e.g. to Done). Diffing status alone
 *  missed every one of those in-between weeks entirely - confirmed live 2026-09-22 against real
 *  data where a Project's percent moved on every one of its milestones week over week but status
 *  never changed, so the census showed zero changes despite real, substantial progress. */
function diffMilestones(
  child: Initiative,
  latestReport: WeeklyReport | undefined,
  previousReport: WeeklyReport | undefined,
): CensusMilestoneChange[] {
  const changes: CensusMilestoneChange[] = [];
  const nameOf = (id: string) => (child.milestones || []).find((m) => m.id === id)?.name || id;

  if (latestReport?.milestoneUpdates?.length) {
    const previousById = new Map((previousReport?.milestoneUpdates || []).map((u) => [u.milestoneId, u]));
    latestReport.milestoneUpdates.forEach((u) => {
      const prev = previousById.get(u.milestoneId);
      if (!prev) return;
      const statusChanged = prev.status !== u.status;
      const percentChanged = prev.completionPercent !== u.completionPercent;
      if (statusChanged || percentChanged) {
        changes.push({
          childId: child.initiativeId, childName: child.name,
          milestoneId: u.milestoneId, name: nameOf(u.milestoneId),
          from: prev.status, to: u.status,
          fromPercent: prev.completionPercent, toPercent: u.completionPercent,
          late: false,
        });
      }
    });
  }

  (child.milestones || [])
    .filter((m) => m.targetDate && m.targetDate < today() && m.status !== 'Done')
    .forEach((m) => {
      if (changes.some((c) => c.milestoneId === m.id)) return;
      const pct = m.completionPercent ?? 0;
      changes.push({
        childId: child.initiativeId, childName: child.name,
        milestoneId: m.id, name: m.name,
        from: m.status, to: m.status, fromPercent: pct, toPercent: pct, late: true,
      });
    });

  return changes;
}

/** Requirement deltas for one child - `requirementUpdates` is always sent on submit
 *  (SubmitReportPage.tsx), regardless of weighted-milestone mode, so this diffs cleanly the
 *  same way that page's own Step 3 already does: previous report's recorded status vs this
 *  report's. */
function diffRequirements(
  child: Initiative,
  latestReport: WeeklyReport | undefined,
  previousReport: WeeklyReport | undefined,
): CensusRequirementChange[] {
  if (!latestReport?.requirementUpdates?.length) return [];
  const allRequirements = [...(child.functionalRequirements || []), ...(child.nonFunctionalRequirements || [])];
  const textOf = (id: string) => allRequirements.find((r) => r.id === id)?.text || id;
  const previousById = new Map((previousReport?.requirementUpdates || []).map((u) => [u.requirementId, u.status]));

  return latestReport.requirementUpdates
    .filter((u) => {
      const from = previousById.get(u.requirementId);
      return from !== undefined && from !== u.status;
    })
    .map((u) => ({
      childId: child.initiativeId, childName: child.name,
      requirementId: u.requirementId, text: textOf(u.requirementId),
      from: previousById.get(u.requirementId)!, to: u.status,
    }));
}

/** The "what changed below" census a Programme/Portfolio's own report (and its read-only
 *  Reports tab view) is built from - one hop only, reading each direct child's own two most
 *  recent already-filed reports (already fetched today for the %-delta chip; this just also
 *  keeps the raw report objects). Never reaches past direct children. */
export function buildCensus(
  children: Initiative[],
  latestReportByChild: Record<string, WeeklyReport | undefined>,
  previousReportByChild: Record<string, WeeklyReport | undefined>,
): Census {
  const milestoneChanges = children.flatMap((c) => diffMilestones(c, latestReportByChild[c.initiativeId], previousReportByChild[c.initiativeId]));
  const requirementChanges = children.flatMap((c) => diffRequirements(c, latestReportByChild[c.initiativeId], previousReportByChild[c.initiativeId]));
  // Most-newsworthy-first, since the Reports tab caps how many rows show by default (see
  // InitiativeDetailPage.tsx's CENSUS_ROW_CAP): a late flag or a real status transition (e.g.
  // completing) belongs ahead of a milestone that merely ticked up a couple of points, and among
  // plain percent moves, the biggest jump is more worth a reader's attention than a small one.
  milestoneChanges.sort((a, b) => {
    const rank = (m: CensusMilestoneChange) => (m.late ? 0 : m.from !== m.to ? 1 : 2);
    const rankDiff = rank(a) - rank(b);
    if (rankDiff !== 0) return rankDiff;
    return Math.abs(b.toPercent - b.fromPercent) - Math.abs(a.toPercent - a.fromPercent);
  });
  return { milestoneChanges, requirementChanges, totalChanges: milestoneChanges.length + requirementChanges.length };
}

/** Human-readable "what moved" for one milestone census row - a real status transition when
 *  there is one, otherwise the percent move that's the actual content of most weeks. */
export function formatMilestoneChange(c: CensusMilestoneChange): string {
  return c.from !== c.to ? `${c.from} → ${c.to}` : `${c.fromPercent}% → ${c.toPercent}%`;
}

/** Reshapes a Census into the same `milestoneChanges`/`requirementChanges`/`lateMilestones`
 *  shape `reportsApi.draft()` already expects from SubmitReportPage.tsx - draft_report on the
 *  backend consumes these generically, no Lambda change needed to feed it a rollup instead of
 *  one initiative's own fields. */
export function toDraftChangeLists(census: Census) {
  return {
    milestoneChanges: census.milestoneChanges
      .filter((m) => !m.late)
      .map((m) => ({
        name: `${m.name} (${m.childName})`,
        from: m.from !== m.to ? m.from : `${m.fromPercent}%`,
        to: m.from !== m.to ? m.to : `${m.toPercent}%`,
      })),
    requirementChanges: census.requirementChanges.map((r) => ({ text: `${r.text} (${r.childName})`, from: r.from, to: r.to })),
    lateMilestones: census.milestoneChanges.filter((m) => m.late).map((m) => `${m.name} (${m.childName})`),
  };
}
